import api from "../http";
import {useEffect, useState} from "react";
import {useStore} from "../contexts/AppContext";
import {useAuth} from "../contexts/AuthContext";
import {Avatar, Button, Divider, Dropdown, message, Tooltip, Typography} from "antd";
import {DownOutlined, InfoCircleOutlined} from "@ant-design/icons";
import DebounceSelect from "./ux_ui/DebounceSelect";

const {Text} = Typography;

const DocumentAccess = ({isIamViewer, isAccessDenied}) => {
    const {users, setUsers, mode, row, setRow, needAccess, setNeedAccess} = useStore()
    const [isDocHasResponsible, setIsDocHasResponsible] = useState(false)
    const [isDocHasTransfer, setIsDocHasTransfer] = useState(false)
    const {userInfo} = useAuth()


    useEffect(() => {
        const hasResponsible = row.current_roles_json.some(user => user.role_id === 2);
        const hasTransfer = row.current_roles_json.some(user => user.role_id === 5);
        setIsDocHasResponsible(hasResponsible);
        setIsDocHasTransfer(hasTransfer);
    }, [row.current_roles_json]);


    useEffect(() => {
        const fetchData = async () => {
            try {
                if (mode === "editDocument" && !needAccess.length) {
                    const needAccessUsers = await api.get(`support/document-access-request/${row.id}`);
                    if (needAccessUsers.status === 200) {
                        setNeedAccess(needAccessUsers.data.map(user => {
                            return {
                                email: user.profile_email,
                                profile_id: user.profile_id,
                                role_id: user.role_id,
                                role_title: user.role_title,
                                comment: user.comment,
                            }
                        }))
                    }
                }
            } catch (error) {
                console.log(error)
            }
        }
        fetchData()

    }, [row.id, needAccess.length, setNeedAccess, mode]);


    const featUsers = async word => {
        try {
            if (word) {
                const response = await api.get(`supabase/users/${word}`);
                if (response.status === 200 && response.data.data.length) {
                    setUsers(
                        response.data.data.map(user => ({
                            label: user.email,
                            value: user.id,
                            avatar: 'https://api.dicebear.com/7.x/miniavs/svg?seed=3',
                        }))
                    )
                }
            }
        } catch (e) {
            console.log(e)
        }
    };


    const onSelectUserRole = (role, index, value) => {
        setRow((prevState) => {
            const updatedRoles = [...prevState.current_roles_json];
            updatedRoles[index] = {
                ...updatedRoles[index],
                role_id: value,
                role_title: role,
            };
            return {
                ...prevState,
                current_roles_json: updatedRoles,
            };
        })
    }

    const onNeedAccessUserRole = (role, index, value) => {
        setNeedAccess(prevState => {
            const updatedRoles = [...prevState];

            updatedRoles[index] = {
                ...updatedRoles[index],
                role_id: value,
                role_title: role,
            };

            return updatedRoles;
        });
    };


    const onAddUsersToList = (ids) => {
        setRow(prevState => {
            const oldRoles = Array.isArray(prevState.current_roles_json)
                ? prevState.current_roles_json
                : [];

            const newRoles = ids.map(user_id => {
                const userExist = oldRoles.some(user => user.profile_id === user_id);
                if (userExist) return null;

                const foundUser = users.find(u => u.value === user_id);
                if (!foundUser) return null;

                const {value: id, data} = foundUser;
                const email = data?.email || foundUser.label;

                return {
                    profile_id: id,
                    email,
                    role_id: 3,
                    role_title: "Editor",
                };
            }).filter(Boolean);

            return {
                ...prevState,
                current_roles_json: [...oldRoles, ...newRoles],
            };
        });
    };


    const onUserCreate = async (email) => {
        const re = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
        if (re.test(email)) {
            try {
                const response = await api.post(`supabase/create-user/${email}`);
                if (response.status === 200 && response.data) {
                    const newUser = response.data;
                    setUsers(prev => [
                        ...prev,
                        {
                            label: newUser.email,
                            value: newUser.id,
                            avatar: 'https://api.dicebear.com/7.x/miniavs/svg?seed=3',
                        },
                    ]);
                    message.success(`User "${newUser.email}" created`);
                }
            } catch (error) {
                message.error(error)
            }
        } else {
            message.warning("Incorrect email")
        }
    }

    const onDecisionRequest = async (user, status) => {
        try {
            const response = await api.put(`support/decision-access-request/${row.id}`, {
                ...user,
                role_id: user.role_id,
                requester_id: user.profile_id,
                requester_email: user.email,
                status,
            })
            if (response.status === 200 && status === 'approved') {
                setRow(prevState => ({
                    ...prevState,
                    current_roles_json: [
                        ...prevState.current_roles_json,
                        user,
                    ],
                }));
            }
            setNeedAccess((prevState => prevState.filter(prev => prev.profile_id !== user.profile_id)))
        } catch (e) {
            message.error(e.response.data.message)
        }
    }

    return (
        <>
            <DebounceSelect
                mode="multiple"
                style={{width: '100%'}}
                placeholder="Select or enter new"
                fetchOptions={featUsers}
                onUserCreate={onUserCreate}
                options={users}
                onAddUsersToList={onAddUsersToList}
                isIamViewer={isIamViewer}
            />
            <Divider/>
            {row.current_roles_json.map((user, index) => {
                const menuItems = [
                    {
                        key: 4,
                        label: "Viewer",
                        onClick: () => onSelectUserRole("Viewer", index, 4),
                        disabled: isIamViewer
                    },
                    {
                        key: 3,
                        label: "Editor",
                        onClick: () => onSelectUserRole("Editor", index, 3),
                        disabled: isIamViewer
                    },
                    !isDocHasResponsible && {
                        key: 2,
                        label: "Responsible",
                        onClick: () => onSelectUserRole("Responsible", index, 2),
                        disabled: isIamViewer
                    },
                    mode === "editDocument" && {type: "divider"},
                    mode === "editDocument" && !isDocHasTransfer &&
                    row.current_roles_json.find(u => u.profile_id === userInfo.profile_id && u.role_id === 1) && {
                        key: 5,
                        label: "Transfer ownership",
                        onClick: () => onSelectUserRole("Transfer ownership", index, 5),
                        disabled: isIamViewer
                    },
                    mode === "editDocument" && {
                        key: 0,
                        label: "Denied",
                        onClick: () => onSelectUserRole("Denied", index, 0),
                    }
                ].filter(Boolean);


                return (
                    <div key={index} style={{display: "flex", alignItems: "center", padding: 10}}>
                        <Avatar
                            size="middle"
                            src={"https://api.dicebear.com/7.x/miniavs/svg?seed=3"}
                            style={{backgroundColor: "#3498ff", marginRight: 10}}
                        />
                        <div style={{flex: 1}}>
                            <Text>{user.email}</Text>
                            <Text type="secondary">
                                {user.profile_id === userInfo.profile_id ? " (you)" : ""}
                            </Text>
                        </div>

                        {user.role_id !== 1 ? (
                            <Dropdown
                                menu={{items: menuItems}}
                                placement="bottomRight"
                                disabled={(isIamViewer && user.role_id !== 4) || isAccessDenied}
                            >
                                <div style={{cursor: "pointer", display: "flex", alignItems: "center", gap: 4}}>
                                    <span>{user.role_title}</span>
                                    <DownOutlined/>
                                </div>
                            </Dropdown>
                        ) : (
                            <Text type="secondary" style={{fontSize: 12}}>
                                {isDocHasResponsible ? "Owner" : "Owner (responsible)"}
                            </Text>
                        )}
                    </div>
                );
            })}
            {needAccess.length > 0 &&
                <>
                    <Divider>Need Access</Divider>
                    {needAccess.map((user, index) => {
                        const menuItems = [
                            {
                                key: 4,
                                label: "Viewer",
                                onClick: () => onNeedAccessUserRole("Viewer", index, 4),
                            },
                            {
                                key: 3,
                                label: "Editor",
                                onClick: () => onNeedAccessUserRole("Editor", index, 3),
                            },
                        ].filter(Boolean);

                        return (
                            <div key={index} style={{display: "flex", alignItems: "center", padding: 10}}>
                                <Avatar
                                    size="middle"
                                    src={"https://api.dicebear.com/7.x/miniavs/svg?seed=3"}
                                    style={{backgroundColor: "#3498ff", marginRight: 10}}
                                />
                                <div style={{flex: 1}}>
                                    <Text>{user.email}</Text>
                                    <Text type="secondary">
                                        {user.profile_id === userInfo.profile_id ? " (you)" : ""}
                                    </Text>
                                </div>

                                <div style={{display: "flex", gap: 20}}>
                                    <Dropdown
                                        menu={{items: menuItems}}
                                        placement="bottomRight"
                                    >
                                        <div style={{cursor: "pointer", display: "flex", alignItems: "center", gap: 4}}>
                                            <span>{user.role_title}</span>
                                            <DownOutlined/>
                                        </div>
                                    </Dropdown>
                                    <Tooltip title={user.comment}>
                                        <InfoCircleOutlined/>
                                    </Tooltip>
                                    <Button
                                        key="rejected"
                                        type="primary"
                                        danger
                                        onClick={() => onDecisionRequest(user, "rejected")}
                                    >
                                        Reject
                                    </Button>
                                    <Button
                                        key="approve"
                                        type="primary"
                                        onClick={() => onDecisionRequest(user,"approved")}
                                    >
                                        Approve
                                    </Button>
                                </div>

                            </div>
                        )
                    })
                    }
                </>
            }
        </>
    )
}

export default DocumentAccess;